import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, SITE, SERVICES, DESTINATIONS } from "@/constants/site";
import { Reveal } from "./Reveal";

const EMPTY = { name: "", email: "", phone: "", service: "", destination: "", message: "" };

const CONTACTS = [
  { icon: Phone, label: "Call Us", value: SITE.phone, href: SITE.phoneHref, testid: "contact-phone-link" },
  { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email-link" },
  { icon: MessageCircle, label: "WhatsApp", value: "Chat with a counsellor", href: SITE.whatsapp, testid: "contact-whatsapp-link" },
  { icon: MapPin, label: "Office", value: SITE.city, href: null, testid: "contact-office" },
];

const INPUT_CLS = "bg-white h-12 rounded-xl border-slate-300 focus-visible:ring-amber-500";

export const EnquiryForm = ({ prefill }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, ...prefill }));
  }, [prefill]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.destination) {
      toast.error("Please select a service and destination preference.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received! Our counsellors will reach out within 24 hours.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-20 lg:py-28 bg-white border-t border-slate-900/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-amber-800">06 — Get In Touch</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B192C] mt-3 leading-[1.05]">
            Book your <em className="italic text-amber-600">free</em> counselling session
          </h2>
        </Reveal>
        <div className="grid lg:grid-cols-5 gap-12 mt-12">
          <Reveal className="lg:col-span-2">
            <p className="text-slate-600 text-base leading-relaxed">
              Tell us where you want to go — a counsellor will call you back with a personalised roadmap.
            </p>
            <div className="mt-8 space-y-5">
              {CONTACTS.map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-amber-700" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{c.label}</p>
                    {c.href ? (
                      <a data-testid={c.testid} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                        className="text-sm font-semibold text-[#0B192C] hover:text-amber-700 transition-colors">{c.value}</a>
                    ) : (
                      <p data-testid={c.testid} className="text-sm font-semibold text-[#0B192C]">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-xl overflow-hidden border border-slate-900/10 shadow-sm">
              <iframe
                data-testid="office-map"
                title="Orion Edcon Services office — Sector 47, Gurugram"
                src="https://www.google.com/maps?q=201%20B%20The%20Olive%20Crescent%2C%20GH%2012%2C%20Sector%2047%2C%20Gurugram%20122018&output=embed"
                className="w-full h-56"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-3">
            <form data-testid="enquiry-form" onSubmit={submit}
              className="bg-[#F8FAFC] border border-slate-900/10 rounded-2xl p-6 sm:p-9 shadow-sm space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Input data-testid="enquiry-name-input" placeholder="Full Name *" required value={form.name} onChange={set("name")} className={INPUT_CLS} />
                <Input data-testid="enquiry-email-input" type="email" placeholder="Email Address *" required value={form.email} onChange={set("email")} className={INPUT_CLS} />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Input data-testid="enquiry-phone-input" placeholder="Phone (with country code) *" required value={form.phone} onChange={set("phone")} className={INPUT_CLS} />
                <Select value={form.destination} onValueChange={set("destination")}>
                  <SelectTrigger data-testid="enquiry-destination-select" className={INPUT_CLS}>
                    <SelectValue placeholder="Destination Preference *" />
                  </SelectTrigger>
                  <SelectContent>
                    {DESTINATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger data-testid="enquiry-service-select" className={INPUT_CLS}>
                  <SelectValue placeholder="Interested Service *" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea data-testid="enquiry-message-input" placeholder="Tell us about your goals (optional)" rows={4}
                value={form.message} onChange={set("message")} className="bg-white rounded-xl border-slate-300 focus-visible:ring-amber-500 resize-none" />
              <Button data-testid="enquiry-submit-btn" type="submit" disabled={loading}
                className="w-full h-12 rounded-full bg-[#0B192C] hover:bg-amber-600 text-white font-semibold text-base transition-colors duration-300">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Send className="w-4 h-4 mr-2" /> Submit Enquiry</>)}
              </Button>
              <p className="text-xs text-slate-400 text-center">By submitting, you agree to be contacted by Orion Edcon Services.</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
